import Styles from './SourceMaterialSelect.module.scss'
import { KnowledgeBaseSourceMaterial } from 'types'
import { SourceMaterial as SourceMaterialIcon } from 'almanac-ui/icons/24/SourceMaterial'
import { Item } from '@react-stately/collections'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Plus } from 'blaze-ui/icons'
import { Menu } from 'components/Menu/Menu'
import { useOpenSourceMaterialModal } from 'hooks/useOpenSourceMaterialModal'
import { DirectionalChevron } from 'components/DirectionalChevron/DirectionalChevron'
import { useState } from 'react'
import { useModals } from 'almanac-ui/Modal'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ReactComponent } from 'react-hotkeys'
import { Button } from 'blaze-ui/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  onChange: (sourceMaterials?: number[]) => void
  selectedSourceMaterials?: number[]
  sourceMaterials: KnowledgeBaseSourceMaterial[]
  ButtonComponent?: ReactComponent
  popoverClassName?: string
}

export const SourceMaterialSelect = ({
  onChange,
  selectedSourceMaterials,
  sourceMaterials,
  ButtonComponent = Button,
  popoverClassName,
}: Props) => {
  const selectedKeys = selectedSourceMaterials?.map((id) => id.toString())
  const openSourceMaterialModal = useOpenSourceMaterialModal()
  const { isMobile } = useResponsiveDesign()
  const selectedSourceMaterialObjects = sourceMaterials.filter((material) =>
    selectedSourceMaterials?.includes(material.id),
  )
  const [isOpen, setIsOpen] = useState(false)
  const { closeStack } = useModals()

  const updateSelectedSourceMaterials = (id: number) => {
    const set = new Set(selectedSourceMaterials)
    set.has(id) ? set.delete(id) : set.add(id)
    onChange(Array.from(set))
  }

  const onHandleSourceMaterialSuccess = (sourceMaterial: KnowledgeBaseSourceMaterial) => {
    updateSelectedSourceMaterials(sourceMaterial.id)
    closeStack()
  }

  return (
    <MenuTrigger onOpenChange={setIsOpen} popoverClassName={popoverClassName} placement="top">
      <ButtonComponent
        endIcon={() => <DirectionalChevron direction={isOpen ? 'up' : 'down'} />}
        variant="secondary"
        aria-label="Select your source materials"
        size="lg"
        justifyContent="space-between"
        fullWidth={isMobile}
      >
        <span className={Styles.buttonText}>
          {selectedSourceMaterialObjects.length === 0
            ? 'Pick up to 3 source materials'
            : selectedSourceMaterialObjects.map((material) => material.name).join(', ')}
        </span>
      </ButtonComponent>
      <Menu
        selectionMode="multiple"
        onAction={(key) => {
          const id = parseInt(key as string)
          if (selectedSourceMaterials?.length === 3 && !selectedSourceMaterials.includes(id)) {
            showToastMessage('You can only include up to 3 source materials', 'error')
            return
          }
          if (key === 'add_new') {
            return openSourceMaterialModal({ onHandleSourceMaterialSuccess }, { newStack: true })
          }
          updateSelectedSourceMaterials(id)
        }}
        selectedKeys={selectedKeys}
      >
        {sourceMaterials.map((sourceMaterial) => {
          return (
            <Item key={sourceMaterial.id} textValue={sourceMaterial.name}>
              <span className={Styles.sourceMaterialIcon}>
                {sourceMaterial.emoji ? <>{sourceMaterial.emoji}</> : <SourceMaterialIcon />}
              </span>
              {sourceMaterial.name}
            </Item>
          )
        })}
        <Item key="add_new">
          <span className={Styles.createSourceMaterial}>
            <Plus color="var(--selected-fill)" /> Add New Source Material
          </span>
        </Item>
      </Menu>
    </MenuTrigger>
  )
}
